import { useState } from "react";
import { useFieldArray, Controller } from "react-hook-form";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { Button, TextField } from "../../../shared/components/ui";
import { SelectCostType } from "../../../shared/components/formulario/select/SelectCostType";
import { UnitOfMeasureEnum } from "../../../shared/components/formulario/select/SelectUnitOfMeasure";
import { ItemEditor } from "./ItemEditor";
import { calculateStepTotal } from "../utils/calculations";

interface StepEditorProps {
    index: number;
    methods: any;
    removeStep: (index: number) => void;
    disabled?: boolean;
}

// Utilitário para formatar valores monetários
function formatCurrency(value: number, currency: string = 'BRL', locale: string = 'pt-BR') {
    return value.toLocaleString(locale, { style: 'currency', currency });
}

export function StepEditor({ index, methods, removeStep, disabled }: StepEditorProps) {
    const { control } = methods;
    const fieldArrayName = `steps.${index}.itens`; // Ensure we are using 'itens'
    const { fields, append, remove } = useFieldArray({ control, name: fieldArrayName });
    const step = methods.watch(`steps.${index}`) || {};
    const stepTotal = calculateStepTotal(step);
    const [open, setOpen] = useState(true);

    return (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 sm:px-5"
            >
                <div className="flex min-w-0 items-center gap-2">
                    <ChevronDown
                        className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 dark:text-slate-500 ${open ? "rotate-0" : "-rotate-90"}`}
                    />
                    <span className="truncate text-base font-semibold text-primary-700 dark:text-primary-400">
                        Etapa {index + 1}
                        {step?.description ? (
                            <span className="ml-2 truncate font-normal text-slate-500 dark:text-slate-400">
                                — {step.description}
                            </span>
                        ) : null}
                    </span>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                    <span className="text-sm font-bold text-secondary-600 dark:text-secondary-400">
                        {formatCurrency(stepTotal)}
                    </span>
                    <span
                        role="button"
                        tabIndex={0}
                        aria-label="Excluir etapa"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!disabled) removeStep(index);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.stopPropagation();
                                if (!disabled) removeStep(index);
                            }
                        }}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300 ${disabled ? "pointer-events-none opacity-40" : "cursor-pointer"}`}
                    >
                        <Trash2 className="h-4 w-4" />
                    </span>
                </div>
            </button>

            {open && (
                <div className="border-t border-slate-100 px-4 pb-4 pt-4 sm:px-5 dark:border-slate-700">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
                        <div className="sm:col-span-3">
                            <Controller
                                name={`steps.${index}.description`}
                                control={control}
                                disabled={disabled}
                                defaultValue={""}
                                render={({ field }) => (
                                    <TextField {...field} label="Descrição da Etapa" />
                                )}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <SelectCostType name={`steps.${index}.costType`} methods={methods} disabled={disabled} />
                        </div>
                        <div className="sm:col-span-1">
                            <Controller
                                name={`steps.${index}.mediumTerm`}
                                disabled={disabled}
                                control={control}
                                defaultValue={0}
                                render={({ field }) => (
                                    <TextField {...field} label="Prazo Médio" type="number" />
                                )}
                            />
                        </div>
                    </div>

                    <div className="my-4 border-t border-slate-100 dark:border-slate-700" />

                    <div>
                        <p className="mb-2 text-sm font-semibold text-slate-600 dark:text-slate-300">Itens</p>
                        <div className="flex flex-col gap-2">
                            {fields.map((item, itemIndex) => (
                                <ItemEditor
                                    key={item.id}
                                    stepIndex={index}
                                    itemIndex={itemIndex}
                                    methods={methods}
                                    removeItem={(i: number) => remove(i)}
                                    disabled={disabled}
                                />
                            ))}
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            startIcon={<Plus className="h-4 w-4" />}
                            disabled={disabled}
                            onClick={() => append({ isLabor: false, quantity: 1, unitValue: 0, unitOfMeasure: UnitOfMeasureEnum.LINEAR_METER, description: "" })}
                            className="mt-3"
                        >
                            Adicionar Item
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
